import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import logo from "../assets/logo.png";
import API_BASE_URL from "../config";
import AnnouncementPopup from "../announcement/AnnouncementPopup";
import {
  FaHome,
  FaSearch,
  FaSlidersH,
  FaSignOutAlt,
  FaUserCog,
  FaChevronDown,
  FaExchangeAlt,
} from "react-icons/fa";

/* ─────────────── styles ─────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');

  .enav * { font-family: 'DM Sans', sans-serif; box-sizing: border-box; }

  /* ── top bar ── */
  .enav-topbar {
    background: #ffffff;
    border-bottom: 1px solid #eef0f4;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 8px;
    height: 58px;
    position: relative;
    z-index: 1100;
  }

  /* ── main navbar ── */
  .enav-bar {
    height: 48px;
    display: flex;
    align-items: center;
    padding: 0 24px;
    position: relative;
    z-index: 1050;
    transition: background-color 0.3s ease;
  }
  .enav-bar::after {
    content: '';
    position: absolute;
    bottom: 0; left: 0; right: 0;
    height: 1px;
    background: rgba(255,255,255,0.08);
  }

  /* ── nav item ── */
  .enav-item {
    position: relative;
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 0 14px;
    height: 46px;
    color: #ffffff;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.2px;
    cursor: pointer;
    text-decoration: none;
    transition: color 0.18s ease;
    white-space: nowrap;
    user-select: none;
  }
  .enav-item:hover { color: #ffffff; }
  .enav-item::before {
    content: '';
    position: absolute;
    bottom: 0; left: 14px; right: 14px;
    height: 2px;
    background: rgba(255,255,255,0);
    border-radius: 2px 2px 0 0;
    transition: background 0.18s ease;
  }
  .enav-item:hover::before,
  .enav-item.active::before { background: rgba(255,255,255,0.7); }
  .enav-item .chevron {
    font-size: 10px;
    opacity: 0.7;
    margin-left: 1px;
    transition: transform 0.2s ease, opacity 0.2s;
  }
  .enav-item:hover .chevron { transform: rotate(180deg); opacity: 1; }

  /* ── dropdown panel ── */
  .enav-drop {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    min-width: 190px;
    background: #ffffff;
    border: 1px solid #e8eaf0;
    border-radius: 10px;
    box-shadow: 0 12px 36px rgba(8,2,53,0.13), 0 2px 8px rgba(8,2,53,0.07);
    overflow: visible;
    animation: edropIn 0.17s ease;
    z-index: 2000;
  }
  .enav-drop > a:first-child,
  .enav-drop > .enav-flyout-wrap:first-child > .enav-flyout-row { border-radius: 10px 10px 0 0; }
  .enav-drop > a:last-child,
  .enav-drop > .enav-flyout-wrap:last-child > .enav-flyout-row { border-radius: 0 0 10px 10px; }
  .enav-drop > a:only-child { border-radius: 10px; }
  @keyframes edropIn {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes eflyIn {
    from { opacity: 0; transform: translateX(-6px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  .enav-drop a {
    display: block;
    padding: 9px 16px;
    font-size: 13px;
    font-weight: 500;
    color: #2d3142;
    text-decoration: none;
    transition: background 0.13s, color 0.13s;
    border-left: 3px solid transparent;
    cursor: pointer;
  }
  .enav-drop a:hover {
    background: #f5f6ff;
    color: #080235;
    border-left-color: #5b5fc7;
  }

  .enav-flyout-wrap { position: relative; }
  .enav-flyout-wrap > .enav-flyout-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 9px 16px;
    font-size: 13px;
    font-weight: 500;
    color: #2d3142;
    cursor: pointer;
    border-left: 3px solid transparent;
    transition: background 0.13s, color 0.13s;
    white-space: nowrap;
    user-select: none;
  }
  .enav-flyout-wrap:hover > .enav-flyout-row,
  .enav-flyout-wrap.open > .enav-flyout-row {
    background: #f5f6ff;
    color: #080235;
    border-left-color: #5b5fc7;
  }
  .enav-flyout-wrap > .enav-flyout-row .fly-arrow { font-size: 14px; opacity: 0.5; margin-left: 14px; }

  .enav-flyout-panel {
    position: absolute;
    top: -1px;
    left: 100%;
    min-width: 200px;
    background: #ffffff;
    border: 1px solid #e8eaf0;
    border-radius: 10px;
    box-shadow: 0 12px 36px rgba(8,2,53,0.13), 0 2px 8px rgba(8,2,53,0.07);
    overflow: visible;
    animation: eflyIn 0.15s ease;
    z-index: 3000;
  }
  .enav-flyout-panel > a:first-child,
  .enav-flyout-panel > .enav-flyout-wrap2:first-child > .enav-flyout-row2 { border-radius: 10px 10px 0 0; }
  .enav-flyout-panel > a:last-child,
  .enav-flyout-panel > .enav-flyout-wrap2:last-child > .enav-flyout-row2 { border-radius: 0 0 10px 10px; }
  .enav-flyout-panel > a:only-child { border-radius: 10px; }
  .enav-flyout-panel > a {
    display: block;
    padding: 9px 16px;
    font-size: 13px;
    font-weight: 500;
    color: #2d3142;
    text-decoration: none;
    border-left: 3px solid transparent;
    transition: background 0.13s, color 0.13s;
  }
  .enav-flyout-panel > a:hover {
    background: #f5f6ff;
    color: #080235;
    border-left-color: #5b5fc7;
  }

  .enav-flyout-wrap2 { position: relative; }
  .enav-flyout-wrap2 > .enav-flyout-row2 {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 9px 16px;
    font-size: 13px;
    font-weight: 500;
    color: #2d3142;
    cursor: pointer;
    border-left: 3px solid transparent;
    transition: background 0.13s, color 0.13s;
    white-space: nowrap;
    user-select: none;
  }
  .enav-flyout-wrap2:hover > .enav-flyout-row2,
  .enav-flyout-wrap2.open > .enav-flyout-row2 {
    background: #f5f6ff;
    color: #080235;
    border-left-color: #5b5fc7;
  }
  .enav-flyout-wrap2 > .enav-flyout-row2 .fly-arrow2 { font-size: 14px; opacity: 0.5; margin-left: 14px; }

  .enav-flyout-panel2 {
    position: absolute;
    top: -1px;
    left: 100%;
    min-width: 160px;
    background: #ffffff;
    border: 1px solid #e8eaf0;
    border-radius: 10px;
    box-shadow: 0 12px 36px rgba(8,2,53,0.13), 0 2px 8px rgba(8,2,53,0.07);
    overflow: hidden;
    animation: eflyIn 0.15s ease;
    z-index: 4000;
  }
  .enav-flyout-panel2 a {
    display: block;
    padding: 9px 16px;
    font-size: 13px;
    font-weight: 500;
    color: #2d3142;
    text-decoration: none;
    border-left: 3px solid transparent;
    transition: background 0.13s, color 0.13s;
  }
  .enav-flyout-panel2 a:hover {
    background: #f5f6ff;
    color: #080235;
    border-left-color: #5b5fc7;
  }

  /* ── search ── */
  .enav-search {
    display: flex;
    align-items: center;
    background: rgba(255,255,255,0.12);
    border: 1px solid rgba(255,255,255,0.2);
    border-radius: 22px;
    padding: 0 14px;
    height: 32px;
    gap: 8px;
    transition: background 0.2s, border 0.2s, width 0.3s ease;
    width: 180px;
    margin-left: 12px;
  }
  .enav-search:focus-within {
    background: rgba(255,255,255,0.2);
    border-color: rgba(255,255,255,0.4);
    width: 220px;
  }
  .enav-search input {
    background: transparent;
    border: none;
    outline: none;
    color: #fff;
    font-size: 13px;
    width: 100%;
  }
  .enav-search input::placeholder { color: rgba(255,255,255,0.55); }
  .enav-search svg { color: rgba(255,255,255,0.6); flex-shrink: 0; }

  /* ── profile btn ── */
  .enav-profile-btn {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    padding: 6px 10px;
    border-radius: 10px;
    transition: background 0.15s;
  }
  .enav-profile-btn:hover { background: #f4f5fb; }
  .enav-avatar {
    width: 36px; height: 36px;
    border-radius: 50%;
    border: 2px solid #e8eaf0;
    object-fit: cover;
    flex-shrink: 0;
  }
  .enav-avatar-fallback {
    width: 36px; height: 36px;
    border-radius: 50%;
    border: 2px solid #e8eaf0;
    background: #e8eaf0;
    display: flex; align-items: center; justify-content: center;
    font-weight: 700; font-size: 14px; color: #374151;
    flex-shrink: 0;
  }
  .enav-profile-name { font-size: 14px; font-weight: 600; color: #1a1d2e; line-height: 1.2; }
  .enav-profile-role { font-size: 11px; font-weight: 500; color: #8b90a7; }

  /* ── profile dropdown ── */
  .enav-profile-drop {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    min-width: 220px;
    background: #fff;
    border: 1px solid #e8eaf0;
    border-radius: 12px;
    box-shadow: 0 16px 40px rgba(8,2,53,0.13);
    overflow: hidden;
    animation: edropIn 0.17s ease;
    z-index: 3000;
  }
  .enav-profile-header {
    padding: 14px 16px 10px;
    background: linear-gradient(135deg, #f8f9ff 0%, #eef0fb 100%);
    border-bottom: 1px solid #e8eaf0;
  }
  .enav-profile-header .name { font-size: 14px; font-weight: 700; color: #1a1d2e; }
  .enav-profile-header .role-badge {
    display: inline-block;
    margin-top: 4px;
    padding: 2px 10px;
    font-size: 10.5px;
    font-weight: 600;
    border-radius: 20px;
    background: #080235;
    color: #fff;
    letter-spacing: 0.4px;
    text-transform: uppercase;
  }
  .enav-profile-drop .menu-item {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 16px;
    font-size: 13px; font-weight: 500;
    color: #2d3142;
    cursor: pointer;
    text-decoration: none;
    transition: background 0.13s;
  }
  .enav-profile-drop .menu-item:hover { background: #f5f6ff; }
  .enav-profile-drop .menu-item.danger { color: #dc2626; }
  .enav-profile-drop .menu-item.danger:hover { background: #fff5f5; }
  .enav-profile-drop .divider { height: 1px; background: #f0f1f5; margin: 4px 0; }

  /* ── settings / home btns ── */
  .enav-settings-btn {
    display: flex; align-items: center; gap: 6px;
    padding: 6px 14px;
    border-radius: 8px;
    font-size: 15px; font-weight: 600;
    color: #444b6b;
    cursor: pointer;
    text-decoration: none;
    transition: background 0.15s, color 0.15s;
  }
  .enav-settings-btn:hover { background: #f0f1f8; color: #080235; }
  .enav-home-btn {
    display: flex; align-items: center;
    padding: 7px 10px;
    border-radius: 8px;
    color: #6b7394;
    text-decoration: none;
    transition: background 0.15s, color 0.15s;
  }
  .enav-home-btn:hover { background: #f0f1f8; color: #080235; }

  /* ── role pills ── */
  .role-pill {
    display: inline-block;
    padding: 4px 12px;
    font-size: 11.5px; font-weight: 600;
    border-radius: 20px;
    background: #f0f1f8;
    color: #3a4060;
    cursor: pointer;
    margin: 2px 0;
    transition: background 0.15s, color 0.15s;
    text-decoration: none;
  }
  .role-pill:hover { background: #080235; color: #fff; }
`;

/* ─────────────── component ─────────────── */
const EmployeeNavbar = () => {
  const navigate = useNavigate();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [searchVal, setSearchVal] = useState("");
  const [photoError, setPhotoError] = useState(false);
  const [openFlyout, setOpenFlyout] = useState(null);
  const [openFlyout2, setOpenFlyout2] = useState(null);
  const [profile, setProfile] = useState({
    username: "Employee",
    role: "USER",
    roles: [],
    photo: null,
  });
  const [navbarColor, setNavbarColor] = useState("#080235");
  const [permissions, setPermissions] = useState({});
  const closeTimerRef = useRef(null);

  const userId = localStorage.getItem("userId");
  const employeeId = localStorage.getItem("employeeId");
  const activeRole = localStorage.getItem("activeRole") || "ROLE_USER";

  // ✅ Stamp the correct role on mount so it isn't left over from AdminNavbar
  useEffect(() => {
    localStorage.setItem("activeRole", "ROLE_USER");
  }, []);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const token = localStorage.getItem("token");
        const employeeId = localStorage.getItem("employeeId");

        const res = await fetch(
          `${API_BASE_URL}/api/permissions/${employeeId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const data = await res.json();

        // your response → { pages: { announcement: true } }
        setPermissions(data.pages || {});
      } catch (err) {
        console.error("Permission fetch error:", err);
      }
    };

    fetchPermissions();
  }, []);

  const open = (key) => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setOpenDropdown(key);
  };
  const close = () => {
    closeTimerRef.current = setTimeout(() => {
      setOpenDropdown(null);
      setOpenFlyout(null);
      setOpenFlyout2(null);
    }, 120);
  };
  const keepOpen = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  };

  const handleGoToTimesheet = () => navigate("/timesheetgrid", { state: { employeeId } });

  /* ── auth check ── */
  useEffect(() => {
    const token = localStorage.getItem("token");
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { toast.error("You must be logged in to access this page."); navigate("/"); return; }
    if (!roles.includes("ROLE_USER")) { toast.error("You are not authorized to access this page."); navigate("/"); }
  }, [navigate]);

  /* ── appearance ── */
  useEffect(() => {
    const token = localStorage.getItem("token");
    const uid = localStorage.getItem("userId");
    if (!token || !uid) return;
    fetch(`${API_BASE_URL}/api/appearance/${uid}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { if (d?.navbarColor) setNavbarColor(d.navbarColor); })
      .catch(() => { });
  }, []);

  /* ── company logo ── */
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    fetch(`${API_BASE_URL}/api/company-info`, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } })
      .then(r => r.json())
      .then(d => { if (Array.isArray(d) && d[0]?.logoPath) setCompanyLogo(`${API_BASE_URL}/${d[0].logoPath}`); })
      .catch(() => { });
  }, []);

  /* ── profile + photo ── */
  useEffect(() => {
    const token = localStorage.getItem("token");
    const eid = localStorage.getItem("employeeId");
    if (!token || !eid) return;

    fetch(`${API_BASE_URL}/api/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        if (d) setProfile(p => ({
          ...p,
          username: d.name || p.username,
          roles: d.roles || ["ROLE_USER"],
          role: d.roles?.includes("ROLE_USER") ? "USER" : p.role,
        }));
      }).catch(() => { });

    fetch(`${API_BASE_URL}/api/employees/me/photo`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        if (blob.size > 0) setProfile(p => ({ ...p, photo: URL.createObjectURL(blob) }));
        else setPhotoError(true);
      })
      .catch(() => setPhotoError(true));
  }, []);

  /* ── avatar ── */
  const Avatar = () => {
    if (!photoError && profile.photo)
      return <img src={profile.photo} alt="avatar" className="enav-avatar" onError={() => setPhotoError(true)} />;
    return (
      <div className="enav-avatar-fallback">
        {(profile.username || "U").charAt(0).toUpperCase()}
      </div>
    );
  };

  /* ── nav items ── */
  const navItems = [
    {
      key: "tasks", label: "Tasks", landing: "/employeetasklanding",
      links: [{ label: "My Tasks", href: "/viewtasks" }],
    },
    {
      key: "timesheets", label: "Timesheets", landing: "/employeetimesheetlanding",
      links: [{ label: "My Timesheets", onClick: handleGoToTimesheet }],
    },
    {
      key: "docs", label: "Documents", landing: "/employeedocumentslanding",
      links: [
        { label: "Upload Documents", href: "/uploaddocuments" },
        { label: "My Documents", href: "/viewdocuments" },
      ],
    },
    {
      key: "leave", label: "Leave", landing: "/employeeleavelanding",
      links: [
        { label: "Leave Application", href: "/leave" },
        { label: "My Leaves", href: "/allleaves" },
      ],
    },
    {
      key: "details", label: "Details", landing: "/employeedetailslanding",
      links: [
        { label: "View Details", onClick: (e) => { e.preventDefault(); navigate("/viewdetails", { state: { employeeId } }); } },
        { label: "Holiday Calendar", href: "/holidays" },
      ],
    },

    {
      key: "assets", label: "Assets", landing: "/emp-assets",
      links: [
        // { label: "Overview", href: "/emp-assets" },
        { label: "My Assets", href: "/myassets" },
        { label: "Asset Requests", href: "/empassets" },
      ],
    },

    ...(permissions?.asset_management
      ? [{
        key: "asset mgmt",
        label: "Asset Management",
        landing: "/admin-assets",
        links: [
          {
            label: "Asset Register",
            children: [
              { label: "All Assets", href: "/allassets", grandchildren: [{ label: "New", href: "/addasset" }] },
              { label: "Allocations", href: "/asset-allocations", grandchildren: [{ label: "New", href: "/newallocation" }] },
              { label: "Transfers", href: "/asset-transfers", grandchildren: [{ label: "New", href: "/newtransfer" }] },
              { label: "Maintenance", href: "/asset-maintenance", grandchildren: [{ label: "New", href: "/newmaintenance" }] },
              { label: "Audit", href: "/asset-audit", grandchildren: [{ label: "New", href: "/newauditrecord" }] },
              { label: "Field Master", href: "/asset-field-master" },
              { label: "Reports", href: "/asset-reports" },
              { label: "Vendors", href: "/vendors", grandchildren: [{ label: "New", href: "/addvendor" }] },
            ],
          },
        ],
      }]
      : []),

    {
      key: "alerts",
      label: "Alerts",
      landing: "/alertslanding",
      links: [
        ...(permissions?.announcement
          ? [{
            label: "Announcement",
            onClick: (e) => {
              e.preventDefault();
              navigate("/announcement", { state: { employeeId } });
            },
          }]
          : []),
        
      ],
    },

    {
      key: "expenses", label: "Expenses", landing: "/emp-expenses",
      links: [
        { label: "Expense Dashboard", href: "/emp-expenses" },
      ],
    },

    {
      key: "evaluation",
      label: "Evaluation",
      landing: "/evaluation",
      links: [
        ...(permissions?.employee_evaluation
          ? [{
            label: "Evaluation",
            onClick: (e) => {
              e.preventDefault();
              navigate("/evaluation");
            },
          }]
          : []),
        
      ],
    },

    

  ];

  const Level3Panel = ({ grandchildren, flyKey2 }) =>
    openFlyout2 === flyKey2 ? (
      <div className="enav-flyout-panel2">
        {grandchildren.map((gc, k) => (
          <a
            key={k}
            href={gc.href}
            onClick={(e) => {
              e.preventDefault();
              setOpenDropdown(null);
              setOpenFlyout(null);
              setOpenFlyout2(null);
              navigate(gc.href);
            }}
          >
            {gc.label}
          </a>
        ))}
      </div>
    ) : null;

  const Level2Panel = ({ children, itemKey, lnkIdx }) =>
    openFlyout === `${itemKey}-${lnkIdx}` ? (
      <div className="enav-flyout-panel" onMouseEnter={keepOpen}>
        {children.map((child, j) => {
          const flyKey2 = `${itemKey}-${lnkIdx}-${j}`;
          if (child.grandchildren) {
            return (
              <div
                key={j}
                className={`enav-flyout-wrap2${openFlyout2 === flyKey2 ? " open" : ""}`}
                onMouseEnter={() => {
                  keepOpen();
                  setOpenFlyout2(flyKey2);
                }}
                onMouseLeave={() => setOpenFlyout2(null)}
              >
                <div
                  className="enav-flyout-row2"
                  onClick={() => {
                    if (child.href) {
                      setOpenDropdown(null);
                      setOpenFlyout(null);
                      setOpenFlyout2(null);
                      navigate(child.href);
                    }
                  }}
                >
                  {child.label}
                  <span className="fly-arrow2">›</span>
                </div>
                <Level3Panel grandchildren={child.grandchildren} flyKey2={flyKey2} />
              </div>
            );
          }

          return (
            <a
              key={j}
              href={child.href}
              onClick={(e) => {
                e.preventDefault();
                setOpenDropdown(null);
                setOpenFlyout(null);
                navigate(child.href);
              }}
            >
              {child.label}
            </a>
          );
        })}
      </div>
    ) : null;

  const renderLinks = (item) =>
    item.links.map((lnk, i) => {
      if (lnk.children) {
        const flyKey = `${item.key}-${i}`;
        return (
          <div
            key={i}
            className={`enav-flyout-wrap${openFlyout === flyKey ? " open" : ""}`}
            onMouseEnter={() => {
              keepOpen();
              setOpenFlyout(flyKey);
              setOpenFlyout2(null);
            }}
            onMouseLeave={() => {
              setOpenFlyout(null);
              setOpenFlyout2(null);
            }}
          >
            <div
              className="enav-flyout-row"
              onClick={() => {
                if (lnk.href) {
                  setOpenDropdown(null);
                  setOpenFlyout(null);
                  setOpenFlyout2(null);
                  navigate(lnk.href);
                }
              }}
            >
              {lnk.label}
              <span className="fly-arrow">›</span>
            </div>
            <Level2Panel children={lnk.children} itemKey={item.key} lnkIdx={i} />
          </div>
        );
      }

      if (lnk.onClick) {
        return (
          <a
            key={i}
            href="#"
            onClick={(e) => {
              e.preventDefault();
              lnk.onClick(e);
            }}
          >
            {lnk.label}
          </a>
        );
      }

      return <a key={i} href={lnk.href}>{lnk.label}</a>;
    });

  return (

    <>
      <AnnouncementPopup />
      <div className="enav">
        <style>{STYLES}</style>

        {/* ════════ TOP BAR ════════ */}
        <div className="enav-topbar">
          {/* Left: logos */}
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <img src={logo} style={{ height: 39 }} alt="logo" />
            {companyLogo && (
              <img src={companyLogo} alt="company"
                style={{ height: 37, width: "auto", objectFit: "contain", borderRadius: 4, borderLeft: "2px solid #eef0f4", paddingLeft: 16 }} />
            )}
          </div>

          {/* Right: home + settings + profile */}
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {/* Home */}
            <a href="/empdashboard" className="enav-home-btn" title="Home">
              <FaHome size={20} />
            </a>

            {/* Settings */}
            <a href="/empsettingpage" className="enav-settings-btn">
              <FaSlidersH size={15} /> Settings
            </a>

            {/* Divider */}
            <div style={{ width: 1, height: 32, background: "#eef0f4", margin: "0 8px" }} />

            {/* Profile */}
            <div style={{ position: "relative" }} onMouseEnter={() => open("profile")} onMouseLeave={close}>
              <div className="enav-profile-btn">
                <Avatar />
                <div>
                  <div className="enav-profile-name">{profile.username}</div>
                  <div className="enav-profile-role">
                    {activeRole?.replace("ROLE_", "")}
                  </div>
                </div>
                <FaChevronDown size={10} style={{
                  opacity: 0.5, marginLeft: 4, transition: "transform 0.2s",
                  transform: openDropdown === "profile" ? "rotate(180deg)" : "none"
                }} />
              </div>

              {openDropdown === "profile" && (
                <div className="enav-profile-drop">
                  {/* header */}
                  <div className="enav-profile-header">
                    <div className="name">{profile.username}</div>
                    <span className="role-badge">{activeRole?.replace("ROLE_", "")}</span>
                  </div>

                  <a className="menu-item" href="/editprofile">
                    <FaUserCog size={14} style={{ opacity: 0.7 }} /> Profile Settings
                  </a>

                  {/* Role switch */}
                  {profile.roles?.length > 1 && (
                    <>
                      <div className="divider" />
                      <div style={{ padding: "8px 16px" }}>
                        <div style={{ fontSize: 10.5, fontWeight: 700, color: "#9ea3bb", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 6 }}>
                          <FaExchangeAlt style={{ marginRight: 5, opacity: 0.7 }} />Switch Role
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {profile.roles.filter(r => r !== activeRole).map((role, i) => {
                            let path = "/";
                            if (role === "ROLE_MANAGER") path = "/managerdashboard";
                            if (role === "ROLE_ADMIN") path = "/admindashboard";
                            if (role === "ROLE_USER") path = "/empdashboard";
                            return (
                              <a key={i} className="role-pill" href={path}
                                onClick={e => {
                                  e.preventDefault();
                                  localStorage.setItem("activeRole", role);
                                  setProfile(p => ({ ...p, role }));
                                  navigate(path);
                                }}>
                                {role.replace("ROLE_", "")}
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}

                  <div className="divider" />
                  <div className="menu-item danger"
                    onClick={() => { sessionStorage.clear(); localStorage.clear(); navigate("/"); }}>
                    <FaSignOutAlt size={13} /> Logout
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ════════ MAIN NAV BAR ════════ */}
        <div className="enav-bar" style={{ backgroundColor: navbarColor }}>
          {(() => {
            const visibleItems = navItems.filter(item => item.links.length > 0);
            const count = visibleItems.length;
            const itemStyle = count > 10
              ? { fontSize: "12px", padding: "0 8px" }
              : count > 8
              ? { fontSize: "13px", padding: "0 10px" }
              : {};
            return (
              <div style={{ display: "flex", alignItems: "center", flex: 1, gap: 2 }}>
                {visibleItems.map(item => (
                  <div
                    key={item.key}
                    style={{ position: "relative" }}
                    onMouseEnter={() => open(item.key)}
                    onMouseLeave={close}
                  >
                    <div
                      className={`enav-item${openDropdown === item.key ? " active" : ""}`}
                      style={itemStyle}
                      onClick={() => navigate(item.landing)}
                    >
                      {item.label}
                      <FaChevronDown className="chevron" />
                    </div>
                    {openDropdown === item.key && (
                      <div className="enav-drop">
                        {renderLinks(item)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}


          {/* Search */}
          <div className="enav-search">
            <FaSearch size={12} />
            <input
              type="text"
              placeholder="Search…"
              value={searchVal}
              onChange={e => setSearchVal(e.target.value)}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default EmployeeNavbar;
